import {
  CatalogCategoryType,
  ItemListOption,
  VariantPackGroup,
} from '@/contexts/item-context-types';
import { sortBadgesByPriority } from '@/components/orders/use-badge-overflow';
import { useEffect, useMemo, useRef } from 'react';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VariantPackGroupType } from '@/enums';
import { VariantPackInfoContext_GroupBadgeFieldsFragment } from '@/types';

interface ItemGroupBadgeFilters {
  category?: CatalogCategoryType;
  categoryItemGroups?: VariantPackGroup[];
  /** When provided, badges are derived from visible items instead of group hierarchy. */
  items?: ItemListOption[];
}

/** Extract unique badges from items' metadata. */
export function deriveBadgesFromItems(
  items: ItemListOption[],
): VariantPackInfoContext_GroupBadgeFieldsFragment[] {
  const uniqueBadges = new Map<
    string,
    VariantPackInfoContext_GroupBadgeFieldsFragment
  >();
  for (const item of items) {
    const itemBadges = item.metadata?.badges;
    if (itemBadges) {
      for (const badge of itemBadges) {
        uniqueBadges.set(badge.uuid, badge);
      }
    }
  }
  return Array.from(uniqueBadges.values());
}

const haveSameBadgeUUIDs = (
  a: VariantPackInfoContext_GroupBadgeFieldsFragment[],
  b: VariantPackInfoContext_GroupBadgeFieldsFragment[],
) => {
  if (a.length !== b.length) {
    return false;
  }

  const bUUIDs = new Set(b.map((badge) => badge.uuid));
  for (const badge of a) {
    if (!bUUIDs.has(badge.uuid)) {
      return false;
    }
  }
  return true;
};

/**
 * Prefer cached (full set) when derived is narrowed by an active filter.
 * Otherwise prefer derived. When lengths are equal, compare UUID sets.
 */
export function resolveBadgesWithCache(
  derived: VariantPackInfoContext_GroupBadgeFieldsFragment[],
  cached: VariantPackInfoContext_GroupBadgeFieldsFragment[],
): VariantPackInfoContext_GroupBadgeFieldsFragment[] {
  if (cached.length > derived.length) {
    return cached;
  }
  if (cached.length < derived.length) {
    return derived;
  }
  return haveSameBadgeUUIDs(derived, cached) ? cached : derived;
}

export const useItemGroupBadgeFilters = ({
  category,
  categoryItemGroups,
  items,
}: ItemGroupBadgeFilters) => {
  const { variantPackGroupMap, childVariantPackGroupMap } =
    useVariantPackInfoContext();

  // Keeps badge options stable when a badge filter narrows the visible items
  const badgeCacheRef = useRef<
    VariantPackInfoContext_GroupBadgeFieldsFragment[]
  >([]);
  const lastCategoryUUIDRef = useRef<string | undefined>(undefined);

  // Synchronously reset cache on category change so we don't render stale badges
  if (category?.uuid !== lastCategoryUUIDRef.current) {
    badgeCacheRef.current = [];
    lastCategoryUUIDRef.current = category?.uuid;
  }

  const itemDerivedBadges = useMemo(
    () => (items ? deriveBadgesFromItems(items) : null),
    [items],
  );

  // Update cache when badge set grows (or changes at same size, e.g. refresh)
  useEffect(() => {
    if (
      itemDerivedBadges &&
      (badgeCacheRef.current.length === 0 ||
        itemDerivedBadges.length > badgeCacheRef.current.length ||
        (itemDerivedBadges.length === badgeCacheRef.current.length &&
          !haveSameBadgeUUIDs(itemDerivedBadges, badgeCacheRef.current)))
    ) {
      badgeCacheRef.current = itemDerivedBadges;
    }
  }, [itemDerivedBadges]);

  // Fallback: hierarchy-based badges when items are not provided
  const hierarchyBadges = useMemo(() => {
    if (items) {
      return null;
    }
    const mainCategoryBadges =
      category?.uuid && variantPackGroupMap[category.uuid]
        ? variantPackGroupMap[category.uuid]!.badges
        : [];

    // find badges associated with the list of filtered categories in the search
    if (categoryItemGroups) {
      const categoryItemGroupsBadges = categoryItemGroups
        .filter((group) => group.type === VariantPackGroupType.Category)
        .flatMap((categoryItemGroup) => categoryItemGroup.badges || []);

      return [...mainCategoryBadges, ...categoryItemGroupsBadges];
    }

    const childCategoryBadges =
      category?.uuid && childVariantPackGroupMap[category.uuid]
        ? childVariantPackGroupMap[category?.uuid!]?.flatMap(
            (uuid) => variantPackGroupMap[uuid]?.badges ?? [],
          )
        : [];

    return [...mainCategoryBadges, ...childCategoryBadges];
  }, [
    items,
    categoryItemGroups,
    variantPackGroupMap,
    category,
    childVariantPackGroupMap,
  ]);

  const badges = useMemo(() => {
    const unsorted = itemDerivedBadges
      ? resolveBadgesWithCache(itemDerivedBadges, badgeCacheRef.current)
      : hierarchyBadges || [];
    return sortBadgesByPriority(unsorted);
  }, [itemDerivedBadges, hierarchyBadges]);

  return {
    badges,
  };
};
